'use client'

import { useMemo } from 'react'
import type { Middleware } from 'swr'
import type { ZodType } from 'zod'

import { studioSwrKey } from '@/features/studio-renew/lib/studio-read'
import {
  studioCollectionSchemas,
  type StudioCollectionResponse,
} from '@/features/studio-renew/schemas/studio-read.schema'
import {
  acceptStudioCollections,
  beginStudioCollectionRead,
  recordStudioCollectionError,
} from '@/features/studio-renew/sync'
import { useRenewStore, type Kind } from '@/features/studio-renew/store'
import { useSessionStore } from '@/features/veluvit/stores/session.store'
import { useCachedSWR } from '@/core/lib/swr/use-cached-swr'

import type { FetcherResponse } from '@/core/lib/fetcher/core'

type Props = { owner: string; kind: Kind; epoch: string }

export function StudioCollectionResource({ owner, kind, epoch }: Props) {
  const key = useMemo(() => studioSwrKey(epoch, kind), [epoch, kind])
  const middleware = useMemo<Middleware>(
    () => (useSWRNext) =>
      function useGuardedSWR(swrKey, fetcher, config) {
        const guardedFetcher =
          fetcher &&
          (async (...args: Parameters<typeof fetcher>) => {
            const read = beginStudioCollectionRead(owner, kind)
            const identity = useSessionStore.getState().identity
            const current = () => {
              const session = useSessionStore.getState()
              return (
                read.current() &&
                useRenewStore.getState().owner === owner &&
                session.identity === identity &&
                (!session.ready ||
                  (session.data?.user?.id === owner &&
                    session.data?.user?.guest === false))
              )
            }
            try {
              const response = await fetcher(...args)
              const collectionResponse =
                response as unknown as FetcherResponse<StudioCollectionResponse>
              if (collectionResponse.ok && current())
                acceptStudioCollections(
                  owner,
                  { [kind]: collectionResponse.data },
                  read.versions
                )
              return response
            } catch (error) {
              if (current()) recordStudioCollectionError(kind, error)
              throw error
            }
          })
        return useSWRNext(swrKey, guardedFetcher, config)
      },
    [kind, owner]
  )

  useCachedSWR<StudioCollectionResponse>(key, 'REALTIME', {
    responseSchema: studioCollectionSchemas[
      kind
    ] as ZodType<StudioCollectionResponse>,
    revalidateOnMount: false,
    use: [middleware],
  })

  return null
}
